'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { formatters } from '@morpho-org/ui-v6/utils';
import { useQuery } from '@tanstack/react-query';
import { ChainConfig } from '@/config';
import { aggregateVaultListItemWarnings } from '@/helpers/aggregateVaultListWarnings';
import { orpc } from '@/lib/orpc/client';
import { VaultList } from '../../VaultList';
import VaultsListSkeleton from './QueryVaultsListSkeleton';
export interface QueryVaultsListProps {
  curatorId: string;
  chainId: ChainId | null;
}
const usdShort = formatters.short.unit('$').digits(2);
const STALE_TIME_MS = 60_000;
const VAULT_V2_VERSION = '2.0';
export default function QueryVaultsList(t0) {
  const $ = _c(16);
  const {
    curatorId,
    chainId
  } = t0;
  let t1;
  if ($[0] !== chainId || $[1] !== curatorId) {
    t1 = orpc.vault.list.queryOptions({
      input: {
        limit: 100,
        curatorIds: [curatorId],
        chainIds: chainId ? [chainId] : ChainConfig.AVAILABLE_CHAIN_IDS,
        totalAssetsUsdGte: 1,
        orderBy: "totalAssetsUsd",
        orderDirection: "desc"
      },
      staleTime: STALE_TIME_MS
    });
    $[0] = chainId;
    $[1] = curatorId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data,
    error,
    isLoading
  } = useQuery(t1);
  if (isLoading) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <VaultsListSkeleton />;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (error) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="gap-2xs p-lg flex min-h-[120px] flex-col items-center justify-center"><span className="text-secondary-foreground text-xs">Couldn't load vaults</span><span className="text-2xs text-muted-foreground">Please try again later</span></div>;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== data?.items) {
    t2 = data?.items ?? [];
    $[5] = data?.items;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = t2.map(_temp);
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const vaults = t3;
  let t4;
  if ($[9] !== vaults) {
    t4 = <VaultList vaults={vaults} />;
    $[9] = vaults;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== data?.partial) {
    t5 = data?.partial && <p data-testid="risk-curator-vaults-partial-notice" className="text-2xs text-muted-foreground px-lg pb-sm">Some vaults could not be loaded, so this list may be incomplete.</p>;
    $[11] = data?.partial;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function _temp(vault) {
  return {
    id: `${vault.chainId}:${vault.address}`,
    vaultName: vault.name,
    address: vault.address,
    imageURI: vault.asset.logoUri,
    chainId: vault.chainId,
    vaultDeposit: usdShort.of(vault.totalAssetsUsd),
    isVaultV2: vault.version === VAULT_V2_VERSION,
    warnings: aggregateVaultListItemWarnings(vault.warnings)
  };
}